import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { axiosInstance } from "./requests";
import { setAccessToken } from "./app.data";
import useUserStore from "../../state-management/useUserStore";
import useCompanyStore from "../../state-management/useCompanyStore";
import { useOrganizationTreeStore } from "../../state-management/useOrganizationTreeStore";
import useEditFormStore from "../../state-management/useEditFormStore";
import ENDPOINTS from "./endpoints";

type QueueItem = {
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
};

let isRefreshing = false;
let failedQueue: QueueItem[] = [];

function processQueue(error: any, token: string | null = null) {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedQueue = [];
}

function isTokenExpiredError(error: any): boolean {
    const status = error?.response?.status;
    const data = error?.response?.data;

    if (status !== 401 && status !== 403) return false;

    return (
        data?.error === "TokenExpiredError" ||
        data?.message === "jwt expired"
    );
}

export function forceSignOut() {
    const userState = useUserStore.getState();
    const companyState = useCompanyStore.getState();
    const orgTreeState = useOrganizationTreeStore.getState();
    const editFormState = useEditFormStore.getState();

    // Save recentCompanyId if the user is an organization user
    if (userState.userType === "ORGANIZATION") {
        localStorage.setItem(
            "recentCompanyId",
            companyState.currentCompany?.id as any
        );
    }

    // Clear all stores
    userState.clearUser();
    orgTreeState.clearOrgTreeStore();
    companyState.clearCompanyStore();
    editFormState.clearFormData();
    setAccessToken("");
    sessionStorage.removeItem("showSidebar");

    // Redirect to sign-in with the current page as prev_page
    const currentPath = window.location.pathname + window.location.search;
    const prevPage = currentPath && currentPath !== "/"
        ? `?prev_page=${encodeURIComponent(currentPath)}`
        : "";

    window.location.href = (process.env.PUBLIC_URL || "") + "/" + prevPage;
}

export function setupAuthInterceptor() {
    axiosInstance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
            const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

            // Only handle token-expired 401/403 errors
            if (!isTokenExpiredError(error)) {
                return Promise.reject(error);
            }

            // Prevent infinite retry loops
            if (originalRequest._retry) {
                return Promise.reject(error);
            }

            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                }).then((token) => {
                    originalRequest.headers = originalRequest.headers || {};
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return axiosInstance(originalRequest);
                });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const refreshToken = useUserStore.getState().currentUser.refresh_token;

                // Use raw axios (not axiosInstance) to avoid interceptor loops
                const response = await axios.post(
                    ENDPOINTS.USER.AUTH.REFRESH_TOKEN,
                    {},
                    { headers: { Authorization: `Bearer ${refreshToken}` } }
                );

                const newTokens = response.data;
                useUserStore.getState().updateUserAuthTokens(newTokens);

                const newAccessToken = newTokens.access_token;

                // Flush the queue — retry all waiting requests with the new token
                processQueue(null, newAccessToken);

                // Retry the original request
                originalRequest.headers = originalRequest.headers || {};
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return axiosInstance(originalRequest);
            } catch (refreshError: any) {
                const data = refreshError?.response?.data;

                if (data?.error?.includes?.("TOKEN_EXPIRED") || data?.error === "TOKEN_EXPIRED") {
                    processQueue(refreshError, null);
                    forceSignOut();
                    return Promise.reject(refreshError);
                }

                processQueue(refreshError, null);
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }
    );
}
